'use client';

import React from 'react';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import aboutUsEramLogo from '@/assets/imgs/about-us-eram-logo.svg';
import aboutUsEramLogoAr from '@/assets/imgs/about-us-eram-logo-ar.svg';
import { studioClient } from '@/sanity/lib/client';
import { localizedAboutUsQuery } from '@/sanity/queries/aboutUs';
import { IAboutUs } from '@/types/aboutUs';

export default function AboutUs() {
  const [aboutUsData, setAboutUsData] = React.useState<IAboutUs | null>(null);
  const [loading, setLoading] = React.useState(true);
  const t = useTranslations('aboutUs');
  const locale = useLocale();

  React.useEffect(() => {
    const fetchAboutUs = async () => {
      try {
        const data = await studioClient.fetch<IAboutUs>(localizedAboutUsQuery(locale));
        setAboutUsData(data);
      } catch (error) {
        console.error('Error fetching about us data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutUs();
  }, [locale]);

  if (loading) {
    return (
      <section className="relative">
        <div className="bg-gradient-to-r from-[#09430A]/10 via-black to-transparent bg-blend-color">
          <Image
            src={aboutUsEramLogo}
            alt="about-us-eram-logo"
            // className="h-auto w-full"
            priority
          />
          <div className="absolute top-1/2 left-1/2 w-full -translate-x-1/2 -translate-y-1/2 px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-7xl text-center">
              <div className="text-base text-white sm:text-lg md:text-xl">{t('loading')}</div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const title = aboutUsData?.title?.[locale as 'en' | 'ar'];
  const description = aboutUsData?.description?.[locale as 'en' | 'ar'];
  // const imageSrc = aboutUsData?.image?.asset?.url || aboutUsEramLogo;
  // const imageAlt = aboutUsData?.image?.alt || 'about-us-eram-logo';
  const imageSrc = locale === 'ar' ? aboutUsEramLogoAr : aboutUsEramLogo;
  const imageAlt = locale === 'ar' ? 'about-us-eram-logo-ar' : 'about-us-eram-logo';

  return (
    <section className="relative">
      <div className="bg-gradient-to-r from-[#09430A]/10 via-black to-transparent bg-blend-color">
        <Image src={imageSrc} alt={imageAlt} priority />
        <div className="absolute top-1/2 left-2 -translate-y-1/2 p-3 lg:left-1/2 lg:-translate-x-1/2">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-4xl lg:max-w-6xl xl:max-w-7xl">
              <h1 className="text-hero-gradient-text text-3xl leading-tight font-bold sm:text-4xl md:text-5xl lg:text-6xl">
                {title}
              </h1>
              <p className="pt-4 text-base leading-relaxed text-white sm:text-lg md:text-xl lg:text-2xl">
                {description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
